import {
  EpisodeAccessModel,
  SeriesModel,
  SeriesAccessModel,
  StripePaymentChargeStatusResponseModel,
  UserMissionItemModel,
  ProductModel,
  UserWalletResponseModel,
} from "@api/models";
import { comiciPlusClient } from "apiClient/ComiciPlusClient";
import { GLOBALS } from "config";
import { ElementModel } from "@api/models";
import { logError } from "./logger";
import { ResponseError } from "@api/runtime";
import { CheckedEpisodeDetail, PushToast } from "app/hooks";
import Cookies from "js-cookie";
import { GOT_LOGIN_BONUS_COOKIE_NAME } from "app/components/AnalyticLogger";

export function formatDate(timeSec?: number): string {
  if (timeSec) {
    const d = new Date(timeSec * 1000);
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  } else {
    return "--";
  }
}
export function formatDateTime(timeSec?: number): string {
  if (timeSec) {
    const d = new Date(timeSec * 1000);
    return (
      d.toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }) +
      " " +
      d.toLocaleTimeString("ja-JP", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  } else {
    return "--";
  }
}
export function formatDateMinimal(
  timeSec: number | undefined,
  omitYear: boolean = false,
  suffix: string = "",
): string {
  if (timeSec) {
    const d = new Date(timeSec * 1000);
    if (omitYear) {
      return `${d.getMonth() + 1}/${d.getDate()}` + suffix;
    } else {
      return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}` + suffix;
    }
  } else {
    return "";
  }
}
/**
 * 「〇時間」「〇分」「〇秒」を返す
 * @param difference 秒
 * @returns
 */
export function formatTime(difference: number): string {
  const hour = Math.floor(difference / (60 * 60));
  const min = Math.floor((difference % (60 * 60)) / 60);

  if (hour) {
    return hour + ":" + String(min).padStart(2, "0");
  } else if (min) {
    return min + "分";
  } else {
    return Math.floor(difference & (60 * 60 * 60)) + "秒";
  }
}
export function formatDateFromDiff(difference: number, suffix: string = ""): string {
  let unitOfTime = "";
  let time = 0;
  if (difference / (60 * 60 * 24 * 365) > 1) {
    // Years
    unitOfTime = "年";
    time = Math.floor(difference / (60 * 60 * 24 * 365));
  } else if (difference / (60 * 60 * 24 * 45) > 1) {
    // Months
    unitOfTime = "ヶ月";
    time = Math.floor(difference / (60 * 60 * 24 * 45));
  } else if (difference / (60 * 60 * 24) > 1) {
    // Days
    unitOfTime = "日";
    time = Math.floor(difference / (60 * 60 * 24));
  } else if (difference / (60 * 60) > 1) {
    // Hours
    unitOfTime = "時間";
    time = Math.floor(difference / (60 * 60));
  } else if (difference / 60 > 1) {
    // Minutes
    unitOfTime = "分";
    time = Math.floor(difference / 60);
  } else {
    // Seconds
    unitOfTime = "秒";
    time = Math.floor(difference);
  }
  return time + unitOfTime + suffix;
}
export function formatDateFromNow(timeSec: number, suffix: string = ""): string {
  if (timeSec) {
    const now = new Date().getTime() / 1000;
    const difference = Math.abs(now - timeSec);
    return formatDateFromDiff(difference);
  } else {
    return "";
  }
}

export function timeRemaining(seconds?: number) {
  if (seconds) {
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) {
      return `${d}日+${h}時間`;
    } else if (h > 0) {
      return `${h}時間`;
    } else if (m > 0) {
      return `${m}分`;
    } else {
      return "1分";
    }
  } else {
    return "";
  }
}

export function twoDigitDate(date?: number): string {
  if (date) {
    return new Date(date * 1000).toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } else {
    return "--";
  }
}

export function dateWithoutYear(date: number) {
  return new Date(date * 1000).toLocaleDateString("ja-JP", { month: "numeric", day: "numeric" });
}

export function dateWithHour(date: number) {
  return new Date(date * 1000).toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * 時差を考慮に入れつつ日本時間で今が何曜日かを数字で返す
 */
export function getDayOfToday(): number {
  const day = new Date(new Date().getTime() + 9 * 60 * 60 * 1000).getUTCDay();
  if (day === 0) {
    return 6;
  } else {
    return day;
  }
}

/**
 * 時差を考慮に入れつつ日本時間における翌日0時ジャストを表すDateを返す
 */
export function getNextDayZeroDate(): Date {
  // 現在の日本時間を取得
  const jstNow = new Date(new Date().getTime() + 9 * 60 * 60 * 1000);

  // 翌日の日本時間0時を計算
  const nextDay = new Date(jstNow);
  nextDay.setUTCDate(nextDay.getUTCDate() + 1);
  nextDay.setUTCHours(0);
  nextDay.setUTCMinutes(0);
  nextDay.setUTCSeconds(0);
  nextDay.setUTCMilliseconds(0);

  // 日本時間の翌日0時をUTCに戻す
  return new Date(nextDay.getTime() - 9 * 60 * 60 * 1000);
}

/**
 * 数値にカンマを入れてフォーマットを整えて文字列を返す
 * @param num 数値
 * @return フォーマットされた文字列
 */
export function formatNumberWithComma(num: number | undefined): string {
  if (num === undefined) {
    return "";
  } else {
    return num.toLocaleString("ja-JP");
  }
}

export type SearchParams = { [key: string]: string | string[] | undefined };

//location.seachから渡ってくるパラメータから特定のintを取得する
export function getUrlSearchParamInt(
  searchParams: SearchParams,
  paramName: string,
  defaultNum: number,
): number {
  const targetParam = searchParams[paramName];
  if (targetParam && !Array.isArray(targetParam) && parseInt(targetParam, 10)) {
    return parseInt(targetParam, 10);
  }
  return defaultNum;
}

//location.seachから渡ってくるパラメータから特定のstringを取得する
export function getUrlSearchParamString(
  searchParams: SearchParams,
  paramName: string,
  defaultStr: string = "",
): string {
  const targetParam = searchParams[paramName];
  if (targetParam && !Array.isArray(targetParam)) {
    return targetParam;
  }
  return defaultStr;
}

//デフォルトのEPリスト（最初5つと最新5つ）を取得する
export async function getDefaultSeriesWithEpisodes(seriesHash: string): Promise<{
  seriesWithFirstEps: SeriesModel | undefined;
  seriesWithLastEps: SeriesModel | undefined;
  episodeTotalCount: number;
  seriesAccess: SeriesAccessModel;
}> {
  try {
    let seriesWithLastEps;
    //シリーズ冒頭5話を取得する
    const seriesWithFirstEps = await comiciPlusClient.contents.getSeriesBySeriesId(
      {
        seriesHash: seriesHash,
        episodeTo: 5,
      },
      { next: { revalidate: GLOBALS.DEFAULT_CACHE_TIME } },
    );
    const seriesAccess = await comiciPlusClient.contents.getSeriesAccessById({
      seriesHash: seriesHash,
      episodeTo: 5,
    });
    const episodeTotalCount = seriesWithFirstEps.summary.numEpisodes;
    const numFirstEpisodes = seriesWithFirstEps.episodes.length;
    const numLastEpisodes = Math.min(5, episodeTotalCount - numFirstEpisodes);
    if (numLastEpisodes) {
      seriesWithLastEps = await comiciPlusClient.contents.getSeriesBySeriesId({
        seriesHash: seriesHash,
        episodeFrom: episodeTotalCount - numLastEpisodes + 1,
        episodeTo: episodeTotalCount,
      });
      const seriesAccessLast = await comiciPlusClient.contents.getSeriesAccessById({
        seriesHash: seriesHash,
        episodeFrom: episodeTotalCount - numLastEpisodes + 1,
        episodeTo: episodeTotalCount,
      });
      if (seriesAccess.episodeAccesses && seriesAccessLast.episodeAccesses) {
        seriesAccess.episodeAccesses = seriesAccess.episodeAccesses?.concat(
          seriesAccessLast.episodeAccesses,
        );
      }
    }
    return { seriesWithFirstEps, seriesWithLastEps, episodeTotalCount, seriesAccess };
  } catch {
    return {
      seriesWithFirstEps: undefined,
      seriesWithLastEps: undefined,
      episodeTotalCount: 0,
      seriesAccess: { seriesId: seriesHash, episodeAccesses: [] },
    };
  }
}

export type EpisodeAccessSet = {
  getEpisodeAccessByEpisodeId: (episodeId: string) => EpisodeAccessModel;
};
export function seriesAccessToepisodeAccessSet(
  seriesAccess: SeriesAccessModel | undefined,
): EpisodeAccessSet {
  const episodeAccessSet: { [key: string]: EpisodeAccessModel } = {};
  if (seriesAccess) {
    seriesAccess.episodeAccesses?.forEach((access) => {
      episodeAccessSet[access.episodeId] = access;
    });
  }
  return {
    getEpisodeAccessByEpisodeId: (episodeId: string) => {
      return episodeAccessSet[episodeId];
    },
  };
}

/**
 * あとどれだけの時間で待つと無料が回復するか
 * @param recoveryUnixTime
 * @returns
 */
export function getWaitAndFreeRecoveryRate(recoveryUnixTime?: number): {
  waitAndFreeRecoveryRate: number;
  waitAndFreeRecoveryTime: number;
} {
  if (recoveryUnixTime) {
    const now = Math.floor(new Date().getTime() / 1000);
    const time = Math.max(0, recoveryUnixTime - now);
    const rate = Math.min(1, time / GLOBALS.WAIT_AND_FREE_RECOVERY) * 100;
    return {
      waitAndFreeRecoveryRate: rate,
      waitAndFreeRecoveryTime: time,
    };
  } else if (recoveryUnixTime !== undefined) {
    //recoveryUnixTimeが0 = チャージ済み
    return {
      waitAndFreeRecoveryRate: 100,
      waitAndFreeRecoveryTime: 0,
    };
  } else {
    return {
      waitAndFreeRecoveryRate: -1,
      waitAndFreeRecoveryTime: -1,
    };
  }
}
export function getWaitAndFreeWaiting(seriesWaitOn: number | undefined) {
  return getWaitAndFreeRecoveryRate(seriesWaitOn).waitAndFreeRecoveryRate !== 100;
}

/**
 * リンクを変換する
 * @param path 管理画面で設定されたリンク先パス
 * @returns Nextjs版向けに調整されたリンク先パス
 */
export function pathConversion(path: string) {
  if (path === "category/manga") {
    if (GLOBALS.HAS_MANGA_WEEK_NAV) {
      return `/category/manga/day/${getDayOfToday()}/1`;
    } else {
      return "/category/manga/1";
    }
  } else if (path === "category/gravure") {
    if (GLOBALS.HAS_GRAVURE_WEEK_NAV) {
      return `/category/gravure/day/${getDayOfToday()}/1`;
    } else {
      return "/category/gravure/1";
    }
  } else if (path === "series/list") {
    return "/" + path + "/up/1";
  } else if (path === "store_items") {
    return "/" + path + "/list/1";
  } else if (path === "news") {
    return "/" + path + "/all/1";
  } else if (path.indexOf("http") !== 0) {
    return "/" + path;
  }
  return path;
}

// 管理ページURLを生成する
export function generateAuthUrl(type: "google" | "twitter") {
  return `${GLOBALS.AUTH_ENDPOINT}/auth/social/${type}/dispatch?callback_url=${GLOBALS.HOME_URL}/auth/callback&origin_domain=${GLOBALS.HOME_URL}`;
}

// descriptionからタグ等を除去したプレーンなテキストを返す

export function stripDescription(description?: string, descriptionType?: number) {
  if (!description) {
    return "";
  }
  if (descriptionType === 1) {
    //新エディタ
    const elm = JSON.parse(description) as ElementModel[];
    return parseDescription(elm);
  } else {
    //旧コンテンツ、HTMLの可
  }
}
function parseDescription(elm: ElementModel[]): string {
  const res = elm.map((item) => {
    if (item.children) {
      return parseDescription(item.children);
    }
    if (item.text) {
      return item.text;
    }
    if (item.html) {
      return item.html.replace(/(<([^>]+)>)/gi, "");
    }
  });
  return res.join("");
}

/**
 * 「クレカ3DS決済コインチャージ状態取得API」を呼び出し、処理中の場合「クレカ3DS決済状態取得」の呼び出しを繰り返す。
 *  statusが'completed' or 'failed'の場合のみStripePaymentChargeStatusResponseModelが返される。
 *  エラー時はundefinedが返される。
 * @param paymentIntentId
 * @returns
 */
export async function pollingChargeStatus(paymentIntentId: string) {
  const startTime = Date.now();

  let isProcessing = false;

  return new Promise<StripePaymentChargeStatusResponseModel>((resolve, reject) => {
    const intervalId = setInterval(async () => {
      // 前回の処理が終わっていなかったらスキップ
      if (isProcessing) return;
      isProcessing = true;

      const currentTime = Date.now();
      // 最初の状態確認から30秒以上経過していたら終了
      if (currentTime - startTime > 30000) {
        clearInterval(intervalId);
        reject(new Error("timeout"));
      }

      /* TODO: 通信環境の不安定なモバイル環境を考慮して、apiのエラー内容によってthrowじゃなくてリトライ検討 */
      try {
        const res = await fetch(
          `/api/checkout/chargeStatus?stripePaymentIntentId=${paymentIntentId}`,
          {
            method: "GET",
          },
        );
        if (res.status !== 200) {
          throw new ResponseError(res);
        }
        const data = (await res.json()).chargeStatus as StripePaymentChargeStatusResponseModel;
        if (data.status === "processing") {
          const res2 = await fetch("/api/checkout", { method: "GET" });
          if (res2.status !== 200) {
            throw new ResponseError(res);
          }
        } else {
          clearInterval(intervalId);
          resolve(data);
        }
      } catch (e) {
        clearInterval(intervalId);
        if (e instanceof Error) {
          logError(e.message);
          reject(e.message);
        }
        reject(new Error("api error"));
      } finally {
        isProcessing = false;
      }
    }, 2000);
  });
}

/**
 * ミッション進捗・達成判定とトースト表示を行う
 * @param missions APIのレスポンスから取得したミッションの配列
 * @param pushToast コンポーネントのトップレベルでpushToastを呼び出したカスタムhook(rules of hooks)
 * @param timeout トースト表示までのタイムアウト
 */
export function controlMissionToast(
  missions: UserMissionItemModel[] | undefined,
  pushToast: PushToast,
  timeout?: number,
) {
  if (missions && missions.length > 0) {
    missions.map((m) => {
      if (
        m.achievementCount === undefined ||
        m.targetAchievementCount === undefined ||
        m.achievementCount > m.targetAchievementCount
      )
        return;

      const toastTitle =
        m.achievementCount === m.targetAchievementCount ? "ミッション達成" : "ミッション進行";
      const toast = () => pushToast(toastTitle, { type: "mission", mission: m });
      if (timeout) {
        setTimeout(() => {
          toast();
        }, timeout);
      } else {
        toast();
      }
    });
  }
}

/**
 * ダッシュボードのドメインを取得する
 * @returns ダッシュボードのドメインを返す
 */
export function getDashboardDomain(): string {
  // TODO:rimacomiやbigcomicsのように、dev環境で共通ドメインを使用しない場合の対応
  if (GLOBALS.ENV === "alpha" || GLOBALS.ENV === "production") {
    return GLOBALS.HOME_URL ? GLOBALS.HOME_URL.replace("https://", "https://dashboard.") : "";
  } else {
    const env = GLOBALS.ENV === "development" ? "dev" : "stg";
    return `https://dashboard-${GLOBALS.APP_ID}-${env}2.${env}.corkbooks.com`;
  }
}

/**
 * チャージ後のポイント+チケット枚数を現在保有数に加算して、まとめてレンタルに必要なポイント・チケット数を計算する
 * @param selectedEpisodes まとめてレンタル予定のエピソード
 * @param userPoints 現在保有のポイント・チケット
 * @param selectedProduct チャージするポイント・チケット
 * @returns チャージ後に消費予定のポイント・チケット数
 */
export function calcNewAmountAndTicketWithCheckout(
  selectedEpisodes: CheckedEpisodeDetail[],
  userPoints: UserWalletResponseModel,
  selectedProduct: ProductModel | undefined,
) {
  let newTotalPrice = 0;
  let newTotalBonusTicket = 0;
  selectedEpisodes.map((ep) => {
    if (ep === null) return;
    if (
      ep.canUseTicket &&
      (userPoints.bonusValue || 0) + (selectedProduct?.points?.bonusValue || 0) >
        newTotalBonusTicket
    ) {
      newTotalBonusTicket += 1;
    } else {
      newTotalPrice += ep.price;
    }
  });
  return {
    newTotalPrice: newTotalPrice,
    newTotalBonusTicket: newTotalBonusTicket,
  };
}
/** ログインボーナス取得のAPIを呼ぶかどうか判定しているCookieの削除。サインアウト時に使用する。 */
export function deleteLoginBonusCookie() {
  if (Cookies.get(GOT_LOGIN_BONUS_COOKIE_NAME)) {
    Cookies.remove(GOT_LOGIN_BONUS_COOKIE_NAME);
  }
}
